import React from 'react';

export type StatusType =
  | 'success'
  | 'warning'
  | 'error'
  | 'info'
  | 'neutral'
  | 'running'
  | 'stopped'
  | 'pending'
  | 'disabled'
  | 'connected'
  | 'disconnected'
  | 'healthy'
  | 'unhealthy';

export type StatusVariant = 'dot' | 'badge' | 'pill' | 'icon';

export interface StatusIndicatorProps {
  status: StatusType;
  label?: string;
  size?: 'small' | 'medium' | 'large';
  variant?: StatusVariant;
  showLabel?: boolean;
  className?: string;
  animated?: boolean;
  tooltip?: string;
}

const statusConfig: Record<StatusType, { color: string; bgColor: string; icon: string; label: string; textClass: string }> = {
  success: { color: '#34b78f', bgColor: '#d4edda', icon: '✓', label: 'Success', textClass: 'text-success-500' },
  warning: { color: '#ffc107', bgColor: '#fff3cd', icon: '⚠', label: 'Warning', textClass: 'text-warning-500' },
  error: { color: '#dc3545', bgColor: '#f8d7da', icon: '✗', label: 'Error', textClass: 'text-danger-500' },
  info: { color: '#17a2b8', bgColor: '#d1ecf1', icon: 'ℹ', label: 'Info', textClass: 'text-info-500' },
  neutral: { color: '#6c757d', bgColor: '#e2e3e5', icon: '●', label: 'Neutral', textClass: 'text-neutral-500 dark:text-neutral-400' },
  running: { color: '#34b78f', bgColor: '#d4edda', icon: '▶', label: 'Running', textClass: 'text-success-500' },
  stopped: { color: '#dc3545', bgColor: '#f8d7da', icon: '⏹', label: 'Stopped', textClass: 'text-danger-500' },
  pending: { color: '#ffc107', bgColor: '#fff3cd', icon: '⏳', label: 'Pending', textClass: 'text-warning-500' },
  disabled: { color: '#6c757d', bgColor: '#e2e3e5', icon: '⏸', label: 'Disabled', textClass: 'text-neutral-500 dark:text-neutral-400' },
  connected: { color: '#34b78f', bgColor: '#d4edda', icon: '🔗', label: 'Connected', textClass: 'text-success-500' },
  disconnected: { color: '#dc3545', bgColor: '#f8d7da', icon: '🔌', label: 'Disconnected', textClass: 'text-danger-500' },
  healthy: { color: '#34b78f', bgColor: '#d4edda', icon: '💚', label: 'Healthy', textClass: 'text-success-500' },
  unhealthy: { color: '#dc3545', bgColor: '#f8d7da', icon: '💔', label: 'Unhealthy', textClass: 'text-danger-500' }
};

const sizeConfig = {
  small: { dot: '8px', font: 'text-xs', padding: 'px-1.5 py-0.5', iconSize: '12px' },
  medium: { dot: '12px', font: 'text-sm', padding: 'px-2 py-1', iconSize: '14px' },
  large: { dot: '16px', font: 'text-base', padding: 'px-3 py-1.5', iconSize: '16px' }
};

export function StatusIndicator({
  status,
  label,
  size = 'medium',
  variant = 'dot',
  showLabel = true,
  className = '',
  animated = false,
  tooltip
}: StatusIndicatorProps) {
  const config = statusConfig[status];
  const sizes = sizeConfig[size];
  const displayLabel = label || config.label;

  const baseClasses = `inline-flex items-center gap-1.5 ${config.textClass} ${animated ? 'animate-pulse' : ''} hover:opacity-80 focus:outline-2 focus:outline-current focus:outline-offset-2 focus:rounded-sm ${className}`;

  const renderIndicator = () => {
    switch (variant) {
      case 'dot':
        return (
          <div className={baseClasses} title={tooltip}>
            <div
              className={`rounded-full ${animated ? 'animate-pulse' : ''}`}
              style={{
                width: sizes.dot,
                height: sizes.dot,
                backgroundColor: config.color
              }}
            />
            {showLabel && (
              <span className={`font-medium whitespace-nowrap ${sizes.font}`}>
                {displayLabel}
              </span>
            )}
          </div>
        );

      case 'badge':
        return (
          <div
            className={`inline-flex items-center gap-1 ${sizes.padding} rounded font-medium ${sizes.font} ${animated ? 'animate-pulse' : ''} ${className}`}
            title={tooltip}
            style={{
              backgroundColor: config.bgColor,
              color: config.color,
              border: `1px solid ${config.color}`
            }}
          >
            <span style={{ fontSize: sizes.iconSize }}>{config.icon}</span>
            {showLabel && <span>{displayLabel}</span>}
          </div>
        );

      case 'pill':
        return (
          <div
            className={`inline-flex items-center gap-1 ${sizes.padding} rounded-full font-medium text-white ${sizes.font} ${animated ? 'animate-pulse' : ''} ${className}`}
            title={tooltip}
            style={{
              backgroundColor: config.color
            }}
          >
            <span style={{ fontSize: sizes.iconSize }}>{config.icon}</span>
            {showLabel && <span>{displayLabel}</span>}
          </div>
        );

      case 'icon':
        return (
          <div className={baseClasses} title={tooltip}>
            <span
              className={animated ? 'animate-pulse' : ''}
              style={{
                fontSize: sizes.iconSize,
                color: config.color
              }}
            >
              {config.icon}
            </span>
            {showLabel && (
              <span
                className={`font-medium whitespace-nowrap ml-1 ${sizes.font}`}
                style={{ color: config.color }}
              >
                {displayLabel}
              </span>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="inline-flex items-center">
      {renderIndicator()}
    </div>
  );
}